import { useEffect, useState } from "react";
import { Download, Printer, Phone, Smartphone, MessageCircle, Loader2, FileText, ClipboardList } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { printProPdf, proPdfUrl as pdfUrl } from "@/lib/printPdf";

const API = process.env.REACT_APP_BACKEND_URL;
const mo = (n) => (n ? `${(n / 1024 / 1024).toFixed(1).replace(".", ",")} Mo` : "");
const tel = (p) => `tel:+33${String(p || "").replace(/\D/g, "").slice(1)}`;
const host = (u) => String(u || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
const printPdf = (setBusy, kind = "print") => printProPdf(kind, setBusy);

function Actions({ sizes }) {
  const [busy, setBusy] = useState(false);
  const share = `https://wa.me/?text=${encodeURIComponent(`Catalogue professionnel Ozentya : ${pdfUrl("mobile")}`)}`;
  const btn = "inline-flex h-14 items-center justify-center gap-3 rounded-full px-7 text-xs font-semibold uppercase tracking-[0.2em] transition-colors";
  return (
    <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
      <a data-testid="pro-catalogue-download" href={pdfUrl("print")} download className={`${btn} bg-[var(--gold)] text-[var(--ink)] hover:bg-[var(--gold-soft)]`}>
        <Download className="h-4 w-4" aria-hidden="true" />Télécharger le catalogue PDF{sizes?.print ? <span className="hidden font-normal normal-case tracking-normal opacity-80 sm:inline">({mo(sizes.print)})</span> : null}
      </a>
      <button data-testid="pro-catalogue-print" type="button" onClick={() => printPdf(setBusy)} disabled={!!busy} className={`${btn} border border-[var(--gold)]/70 text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)] disabled:opacity-60`}>
        {busy === "print" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" aria-hidden="true" />}Imprimer le catalogue
      </button>
      <a data-testid="pro-catalogue-download-short" href={pdfUrl("short")} download className={`${btn} border border-[var(--gold)]/70 text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)]`}>
        <FileText className="h-4 w-4" aria-hidden="true" />Version courte · 5 pages{sizes?.short ? ` (${mo(sizes.short)})` : ""}
      </a>
      <a data-testid="pro-catalogue-download-mobile" href={pdfUrl("mobile")} download className={`${btn} border border-[var(--line)] text-[var(--parchment)] hover:border-[var(--gold)]`}>
        <Smartphone className="h-4 w-4" aria-hidden="true" />Version téléphone{sizes?.mobile ? ` (${mo(sizes.mobile)})` : ""}
      </a>
      <button data-testid="pro-catalogue-print-flyer" type="button" onClick={() => printPdf(setBusy, "flyer")} disabled={!!busy} className={`${btn} border border-[var(--gold)]/70 text-[var(--gold-soft)] hover:bg-[var(--gold)] hover:text-[var(--ink)] disabled:opacity-60`}>
        {busy === "flyer" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" aria-hidden="true" />}Imprimer le flyer
      </button>
      <a data-testid="pro-catalogue-flyer" href={pdfUrl("flyer")} download className={`${btn} border border-[var(--line)] text-[var(--parchment)] hover:border-[var(--gold)]`}>
        <FileText className="h-4 w-4" aria-hidden="true" />Flyer recto-verso{sizes?.flyer ? ` (${mo(sizes.flyer)})` : ""}
      </a>
      <a data-testid="pro-catalogue-order-form" href={pdfUrl("order", true)} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] text-[var(--parchment)] hover:border-[var(--gold)]`}>
        <ClipboardList className="h-4 w-4" aria-hidden="true" />Bon de commande à imprimer
      </a>
      <a data-testid="pro-catalogue-whatsapp" href={share} target="_blank" rel="noopener noreferrer" className={`${btn} border border-[var(--line)] text-[var(--parchment)] hover:border-[var(--gold)]`}>
        <MessageCircle className="h-4 w-4" aria-hidden="true" />Partager sur WhatsApp
      </a>
      <a data-testid="pro-catalogue-print-open" href={pdfUrl("print", true)} target="_blank" rel="noopener noreferrer" className="self-center text-xs text-[var(--parchment)]/80 underline underline-offset-4 hover:text-[var(--gold-soft)] sm:basis-full">
        L'impression ne s'ouvre pas ? Ouvrir le PDF à imprimer
      </a>
      <a data-testid="pro-catalogue-print-short" href={pdfUrl("short", true)} target="_blank" rel="noopener noreferrer" className="self-center text-xs text-[var(--parchment)]/80 underline underline-offset-4 hover:text-[var(--gold-soft)]">
        Ouvrir la version courte (5 pages) à imprimer
      </a>
      <a data-testid="pro-catalogue-print-flyer-open" href={pdfUrl("flyer", true)} target="_blank" rel="noopener noreferrer" className="self-center text-xs text-[var(--parchment)]/80 underline underline-offset-4 hover:text-[var(--gold-soft)]">
        Ouvrir le flyer recto-verso à imprimer
      </a>
    </div>
  );
}

function Range({ cat, items }) {
  return (
    <section data-testid={`pro-catalogue-cat-${cat.key}`} className="border-t border-[var(--line)] py-14">
      <p className="eyebrow">{cat.kicker}</p>
      <h2 className="mt-3 font-display text-3xl md:text-4xl">{cat.title}</h2>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[var(--parchment)]/80 md:text-base">{cat.intro}</p>
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((p) => (
          <article key={p.slug} data-testid={`pro-catalogue-item-${p.slug}`} className="border border-[var(--gold)]/30 bg-[var(--ink-2)]">
            <div className="aspect-square bg-black">
              {p.image ? <img src={imgUrl(p.image)} alt={p.name} loading="lazy" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-xs uppercase tracking-[0.2em] text-[var(--gold-soft)]">Photo à venir</div>}
            </div>
            <div className="p-4">
              <h3 className="font-display text-base leading-snug md:text-lg">{p.name}</h3>
              {p.format ? <p className="mt-1 text-xs uppercase tracking-[0.15em] text-[var(--gold)]">{p.format}</p> : null}
              <p className="mt-2 text-xs leading-relaxed text-[var(--parchment)]/75">{p.on_request || !p.packaging ? "Conditionnement pro sur demande" : p.packaging}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function Order({ data }) {
  const steps = ["Rendez-vous sur notre site professionnel.", "Inscrivez-vous en créant votre compte professionnel.", "Connectez-vous à votre espace professionnel.", "Découvrez nos produits et passez directement votre commande en ligne."];
  return (
    <section data-testid="pro-catalogue-order" className="mt-6 grid gap-10 border border-[var(--gold)]/60 bg-black p-7 md:grid-cols-[1fr_auto] md:p-12">
      <div>
        <p className="eyebrow">Pour toute commande professionnelle</p>
        <ol className="mt-6 space-y-5">
          {steps.map((s, i) => (
            <li key={s} className="flex gap-4"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--gold)] font-display text-[var(--ink)]">{i + 1}</span><span className="pt-1.5 text-sm md:text-base">{s}</span></li>
          ))}
        </ol>
        <a data-testid="pro-catalogue-pro-link" href={data.pro_url} target="_blank" rel="noopener noreferrer" className="mt-8 inline-block font-display text-xl text-[var(--gold-soft)] underline-offset-4 hover:underline md:text-2xl">Inscrivez-vous sur {host(data.pro_url)} et commandez en ligne !</a>
        <p className="mt-3 text-xs text-[var(--parchment)]/75">Les tarifs professionnels sont consultables dans votre espace professionnel.</p>
      </div>
      <a href={data.pro_url} target="_blank" rel="noopener noreferrer" className="self-center justify-self-center border border-[var(--gold)] bg-white p-3">
        <img data-testid="pro-catalogue-qr" src={`${API}/api/pro-catalogue/qr.png`} alt={`QR code vers ${host(data.pro_url)}`} className="h-44 w-44" />
      </a>
    </section>
  );
}

function Contacts({ contacts }) {
  return (
    <section data-testid="pro-catalogue-contacts" className="mt-14">
      <p className="eyebrow">Votre contact commercial Ozentya</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {(contacts || []).map((c, i) => (
          <a key={c.phone} data-testid={`pro-catalogue-contact-${i}`} href={tel(c.phone)} className="flex items-center gap-4 border border-[var(--gold)]/50 bg-[var(--ink-2)] p-5 transition-colors hover:border-[var(--gold)]">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gold)] text-[var(--ink)]"><Phone className="h-5 w-5" aria-hidden="true" /></span>
            <span><span className="block text-xs font-semibold uppercase tracking-[0.2em]">{c.name}</span><span className="mt-1 block font-display text-2xl text-[var(--gold-soft)]">{c.phone}</span></span>
          </a>
        ))}
      </div>
    </section>
  );
}

export default function CataloguePro() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useSeo({ title: "Catalogue professionnel Ozentya 2026 | Coiffure & Barber", description: "Catalogue professionnel Ozentya pour salons de coiffure, coiffeurs et barbiers.", path: "/catalogue-pro", noindex: true });
  useEffect(() => {
    api.get("/pro-catalogue").then((r) => setData(r.data)).catch(() => setFailed(true));
  }, []);
  const products = Array.isArray(data?.products) ? data.products : [];
  const cats = (Array.isArray(data?.categories) ? data.categories : []).map((c) => ({ cat: c, items: products.filter((p) => p.category === c.key) })).filter((g) => g.items.length);

  return (
    <main data-testid="pro-catalogue-page" className="mx-auto max-w-6xl px-5 pb-28 pt-40 md:px-10">
      <p className="eyebrow">{data?.edition || "Édition professionnelle 2026"}</p>
      <h1 data-testid="pro-catalogue-title" className="mt-4 font-display text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">Catalogue professionnel <span className="italic text-[var(--gold-soft)]">Coiffure &amp; Barber</span></h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--parchment)]/85 md:text-lg">Produits professionnels Ozentya pour les salons de coiffure, coiffeurs indépendants, spécialistes de la coiffure afro, barbiers et Barber Shops. {products.length ? `${products.length} références` : ""}</p>
      <Actions sizes={data?.sizes} />
      {failed ? <p data-testid="pro-catalogue-error" className="mt-8 text-sm text-[var(--parchment)]/80">Le détail du catalogue est momentanément indisponible ; le PDF reste téléchargeable.</p> : null}
      {data ? <><div className="mt-16"><Order data={data} /><Contacts contacts={data.contacts} /></div>{cats.map((g) => <Range key={g.cat.key} {...g} />)}</> : null}
    </main>
  );
}
